import { useEffect, useState } from "react";
import { Brain, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * MapIntelligenceChip — small "AI is learning" badge that fetches the live
 * map-intelligence snapshot and surfaces the one-line summary plus a few
 * top-stat tiles. Public endpoint, no auth required.
 *
 * Refreshes every 60s — cheap (~50ms server-side once indexed).
 */
export default function MapIntelligenceChip() {
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/aria/map-intelligence");
        if (!cancelled) setSnap(res.data);
      } catch { /* fail silent — best-effort polish */ }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!snap?.growth) return null;
  const g = snap.growth;
  const peak = snap.peak_hour;
  const pace = snap.avg_minutes_per_km;

  return (
    <div data-testid="map-intel-chip"
      className="flex items-center justify-between gap-3 p-3 border border-purple-500/30 bg-purple-500/[0.07] rounded-md">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-9 h-9 rounded-full bg-purple-500/15 flex items-center justify-center flex-shrink-0">
          <Brain size={16} className="text-purple-300" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-purple-300 flex items-center gap-1.5">
            <TrendingUp size={10} /> AI Map · updated daily
          </p>
          <p data-testid="map-intel-one-liner" className="text-white text-xs font-body truncate">
            {snap.one_line}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-white/60 flex-shrink-0">
        {pace && (
          <div data-testid="map-intel-pace" className="hidden sm:block text-right">
            <p className="text-purple-300 font-display text-sm leading-none">{pace}</p>
            <p>min/km</p>
          </div>
        )}
        {peak?.count > 0 && (
          <div data-testid="map-intel-peak" className="hidden md:block text-right">
            <p className="text-purple-300 font-display text-sm leading-none">{String(peak.hour).padStart(2, "0")}:00</p>
            <p>peak hour</p>
          </div>
        )}
        <div className="text-right">
          <p className="text-purple-300 font-display text-sm leading-none">{g.last_24h}</p>
          <p>today</p>
        </div>
      </div>
    </div>
  );
}
